import type { Route } from "./route";
import type { AnalyticsUser, Destination, DestinationEvent, EventProperties } from "./types";

/**
 * Time and randomness are system boundaries. They live in module state, not in
 * `initAnalytics` options, because events tracked before init need their
 * timestamp and sampling decision at `track` time. Production uses the
 * defaults; tests replace them through `resetAnalytics` in
 * `@visioni/a3s-analytics/testing`, so the main entry point stays unchanged.
 */
export interface Boundaries {
  /** Epoch milliseconds. */
  readonly now: () => number;
  /** A number in [0, 1), like `Math.random`. */
  readonly random: () => number;
}

/** A call made before init, replayed in order once the destination is ready. */
type QueuedCall =
  | { readonly kind: "track"; readonly event: DestinationEvent }
  | { readonly kind: "identify"; readonly user: AnalyticsUser };

/** Before init, while it runs, and while the queue drains: calls wait in the queue. */
interface Queueing {
  readonly kind: "queueing";
  readonly queue: QueuedCall[];
}

type Status =
  | Queueing
  | { readonly kind: "ready"; readonly destination: Destination }
  /** Init failed: events are dropped until `initAnalytics` is called again. */
  | { readonly kind: "failed" };

/** The last route passed to `reportRoute`. */
export interface CurrentRoute {
  readonly screen: string;
  /** Identifies the route by template and params, to tell a new route from a repeat. */
  readonly key: string;
  /** Merged into every event tracked while this route is current. */
  readonly context: EventProperties;
}

/**
 * Whether the app is in the foreground, and whether it has been opened yet in
 * this process. Kept here, not in the lifecycle hook, so a remounted root
 * layout neither resends the cold-start `App Opened` nor repeats a transition.
 */
export interface AppLifecycle {
  inForeground: boolean;
  opened: boolean;
}

/** Where `track` reads the route at the moment of the call; `undefined` when it has none yet. */
export type RouteSource = () => Route | undefined;

interface AnalyticsState {
  readonly boundaries: Boundaries;
  status: Status;
  route: CurrentRoute | undefined;
  readonly lifecycle: AppLifecycle;
  routeSource: RouteSource | undefined;
  /** Whether a route source failure has been logged, so it is logged once. */
  routeSourceFailureLogged: boolean;
  /** The latest user passed to `identify`, replayed to a destination started after a failed init. */
  user: AnalyticsUser;
  /** Whether `identify` was ever called, so a retry replays a logout (`null`) as well as a user. */
  identified: boolean;
  /** The init in flight, returned to repeat `initAnalytics` calls so only one destination is ever started. */
  starting: Promise<void> | undefined;
}

/**
 * Caps memory if init never happens. Only tracked events count; identify calls
 * are always kept so queued events stay attributed. Once full, later events are discarded
 * rather than the earliest: the first events of a launch (App Opened, the
 * first Screen Viewed) are the ones that anchor the session.
 */
const MAX_QUEUED_EVENTS = 200;

const defaultBoundaries: Boundaries = { now: () => Date.now(), random: () => Math.random() };

function createState(boundaries: Boundaries): AnalyticsState {
  return {
    boundaries,
    status: { kind: "queueing", queue: [] },
    route: undefined,
    lifecycle: { inForeground: false, opened: false },
    routeSource: undefined,
    routeSourceFailureLogged: false,
    user: null,
    identified: false,
    starting: undefined,
  };
}

// Module-level singleton: `track` is called from non-React code (API client,
// stores) and the SDK behind the destination is itself global.
let state = createState(defaultBoundaries);

export function replaceState(boundaries: Partial<Boundaries>): void {
  state = createState({ ...defaultBoundaries, ...boundaries });
}

export function now(): number {
  return state.boundaries.now();
}

export function random(): number {
  return state.boundaries.random();
}

export function currentRoute(): CurrentRoute | undefined {
  return state.route;
}

export function appLifecycle(): AppLifecycle {
  return state.lifecycle;
}

export function routeSource(): RouteSource | undefined {
  return state.routeSource;
}

/** Internal: for `useRouteAnalytics` in the React Native entry, not exported from the core. */
export function setRouteSource(source: RouteSource): void {
  state.routeSource = source;
}

/** True the first time it is called for this state, so a failure is logged once. */
export function claimRouteSourceFailureLog(): boolean {
  if (state.routeSourceFailureLogged) {
    return false;
  }
  state.routeSourceFailureLogged = true;
  return true;
}

export function setCurrentRoute(route: CurrentRoute): void {
  state.route = route;
}

export function send(event: DestinationEvent): void {
  const { status } = state;
  switch (status.kind) {
    case "queueing":
      if (status.queue.filter((call) => call.kind === "track").length < MAX_QUEUED_EVENTS) {
        status.queue.push({ kind: "track", event });
      }
      return;
    case "ready":
      deliver(status.destination, { kind: "track", event });
      return;
    case "failed":
      return;
  }
}

export function setUser(user: AnalyticsUser): void {
  state.user = user;
  state.identified = true;
  const { status } = state;
  switch (status.kind) {
    case "queueing":
      status.queue.push({ kind: "identify", user });
      return;
    case "ready":
      deliver(status.destination, { kind: "identify", user });
      return;
    case "failed":
      return;
  }
}

/**
 * Starts `destination` unless one is already starting or started: a repeat
 * call gets the in-flight init (its own destination is ignored), and a call
 * after success is a no-op.
 *
 * A retry after a failed init starts a fresh queue with the current user and
 * then calls `onRetry`, so the new destination starts from the same identity
 * and screen as the app, not from whatever the dropped queue held.
 *
 * The destination's `init()` is called once per start, and only started again
 * after it rejected. Until it settles, every call gets the same in-flight
 * promise and events keep queueing (capped), however long it takes: a late
 * success still starts the destination. `initAnalytics` stops holding up its
 * caller after a bound; this promise does not.
 */
export function start(destination: Destination, onRetry: () => void): Promise<void> {
  if (state.starting !== undefined) {
    return state.starting;
  }
  if (state.status.kind === "ready") {
    return Promise.resolve();
  }
  if (state.status.kind === "failed") {
    state.status = {
      kind: "queueing",
      queue: state.identified ? [{ kind: "identify", user: state.user }] : [],
    };
    onRetry();
  }
  const owner = state;
  const starting = initialise(owner, state.status, destination).finally(() => {
    owner.starting = undefined;
  });
  owner.starting = starting;
  return starting;
}

async function initialise(owner: AnalyticsState, queueing: Queueing, destination: Destination): Promise<void> {
  try {
    await destination.init();
  } catch (error) {
    console.error("[a3s-analytics] Init failed; dropped queued events:", error);
    if (state === owner) {
      owner.status = { kind: "failed" };
    }
    return;
  }
  // `resetAnalytics` replaces the module-level state; an init started before
  // that must not drain the old queue or touch the new state.
  if (state !== owner) {
    return;
  }
  // Stay in "queueing" until the queue is empty, so anything tracked during the
  // drain (e.g. by the destination itself) goes behind the older events.
  for (let call = queueing.queue.shift(); call !== undefined; call = queueing.queue.shift()) {
    deliver(destination, call);
  }
  owner.status = { kind: "ready", destination };
}

/** Analytics must never break the caller, so destination errors are logged and swallowed. */
function deliver(destination: Destination, call: QueuedCall): void {
  try {
    if (call.kind === "track") {
      destination.track(call.event);
    } else {
      destination.identify(call.user);
    }
  } catch (error) {
    console.error(`[a3s-analytics] ${call.kind} failed:`, error);
  }
}
